/**
 * Google Ads Click ID (GCLID) Capture & Persistence
 * 
 * Captures gclid, wbraid, and gbraid from URL parameters when visitors
 * arrive from Google Ads. Stores them in localStorage with a 90-day expiry
 * (matching Google's conversion lookback window) and saves to the database
 * for offline conversion matching.
 */

import { supabase } from "@/integrations/supabase/client";
import { isTrackingEnabled } from "@/lib/trackingEnvironment";

const STORAGE_KEY = "zooi_ad_click";
const EXPIRY_DAYS = 90;

export interface AdClickData {
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landingPage: string;
  sessionId: string;
  /** Correlation id that follows gclid through capture → cart → uploader. */
  chainId?: string;
  timestamp: number;
}


const LS_SESSION_KEY = "zooi_session_id_ls";
const LS_LANDING_KEY = "zooi_landing_page_ls";
const LS_SOURCE_KEY = "zooi_first_source_ls";

/** Read from localStorage fallback when sessionStorage is empty (e.g. after redirect). */
function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* full/blocked */ }
}

/** Generate a simple session ID if none exists. Exported so attribution
 * always has a session id even for non-ad traffic (organic, direct, social).
 * Falls back to localStorage so the ID survives full reloads and checkout redirects. */
export function getOrCreateSessionId(): string {
  const existing = sessionStorage.getItem("zooi_session_id");
  if (existing) return existing;
  // Restore from localStorage (survives redirect/reload)
  const restored = lsGet(LS_SESSION_KEY);
  if (restored) {
    sessionStorage.setItem("zooi_session_id", restored);
    return restored;
  }
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  sessionStorage.setItem("zooi_session_id", id);
  lsSet(LS_SESSION_KEY, id);
  return id;
}

/** Get or create a persistent landing page record for this session.
 * Falls back to localStorage so the landing page survives checkout redirects. */
function getOrCreateLandingPage(): string {
  try {
    const existing = sessionStorage.getItem("zooi_landing_page");
    if (existing) return existing;
    // Restore from localStorage
    const restored = lsGet(LS_LANDING_KEY);
    if (restored) {
      sessionStorage.setItem("zooi_landing_page", restored);
      return restored;
    }
    const lp = window.location.pathname + window.location.search;
    sessionStorage.setItem("zooi_landing_page", lp);
    lsSet(LS_LANDING_KEY, lp);
    return lp;
  } catch {
    return window.location.pathname;
  }
}

/** Read stored ad click data from localStorage */
export function getStoredAdClick(): AdClickData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data: AdClickData = JSON.parse(raw);
    // Check expiry (90 days)
    if (Date.now() - data.timestamp > EXPIRY_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/** Get the current GCLID (or wbraid/gbraid) for conversion tracking */
export function getCurrentGclid(): string | null {
  const data = getStoredAdClick();
  if (!data) return null;
  return data.gclid || data.wbraid || data.gbraid || null;
}

export function getStoredGoogleClickIdentifier(): { type: "gclid" | "gbraid" | "wbraid"; value: string; sessionId: string } | null {
  const data = getStoredAdClick();
  if (!data) return null;
  if (data.gclid) return { type: "gclid", value: data.gclid, sessionId: data.sessionId };
  if (data.gbraid) return { type: "gbraid", value: data.gbraid, sessionId: data.sessionId };
  if (data.wbraid) return { type: "wbraid", value: data.wbraid, sessionId: data.sessionId };
  return null;
}

/**
 * Build Shopify cart attributes from the most recent ad-click data.
 *
 * Rules (per spec):
 *  - Never write empty/null values
 *  - Idempotent: returns the same set for the same localStorage state
 *  - Uses the most recent valid click (getStoredAdClick already enforces 90d expiry)
 *  - Safe when localStorage is empty/blocked → returns []
 *
 * Keys are prefixed with `_` so they appear as hidden note_attributes on the
 * Shopify Order (not visible to the customer in checkout).
 */
export function buildAttributionAttributes(): Array<{ key: string; value: string }> {
  const attrs: Array<{ key: string; value: string }> = [];
  const push = (k: string, v: string | number | undefined | null) => {
    if (v === undefined || v === null) return;
    const s = String(v).trim();
    if (!s) return;
    attrs.push({ key: k, value: s });
  };

  // Always emit session_id + landing_page so EVERY order (organic, direct,
  // social, paid) has cross-device attribution data — not just Google Ads clicks.
  const data = getStoredAdClick();
  try {
    push('_session_id', data?.sessionId || getOrCreateSessionId());
    push('_landing_page', data?.landingPage || getOrCreateLandingPage());
  } catch {
    // sessionStorage blocked — skip silently
  }

  // Ad-click identifiers only when present
  if (data) {
    const hasClickId = !!(data.gclid || data.wbraid || data.gbraid);
    push('_gclid', data.gclid);
    push('_wbraid', data.wbraid);
    push('_gbraid', data.gbraid);
    if (hasClickId) {
      const clickType = data.gclid ? 'gclid' : data.gbraid ? 'gbraid' : 'wbraid';
      const clickValue = data.gclid || data.gbraid || data.wbraid;
      push('_google_click_id', clickValue);
      push('_google_click_id_type', clickType);
      push('_gclid_captured_at', new Date(data.timestamp).toISOString());
    }
    // Correlation id (capture → cart → uploader). Always emit when present,
    // even without a click ID — surfaces the chain on organic/EC-only paths too.
    push('_chain_id', data.chainId);
    // UTM + referrer so Shopify order carries full attribution context.
    // Google click IDs frequently arrive without explicit UTMs; keep the
    // source/medium/campaign triplet complete so fallback attribution is stable.
    push('_utm_source', data.utm_source || (hasClickId ? 'google' : undefined));
    push('_utm_medium', data.utm_medium || (hasClickId ? 'cpc' : undefined));
    push('_utm_campaign', data.utm_campaign || (hasClickId ? 'checkout_attribution' : undefined));
    push('_utm_content', data.utm_content);
    push('_utm_term', data.utm_term);
    push('_referrer', data.referrer);
  }
  return attrs;
}

/**
 * Fire a single trace event in the gclid chain (capture → cart → uploader).
 * Best-effort, non-blocking. Uses fetch + keepalive so it survives navigation
 * (e.g. the redirect to Shopify checkout).
 *
 * `chainId` is optional — when omitted we pull it from the most recent stored
 * ad-click. If no chain id exists, the call is a no-op (we don't fabricate one).
 */
export function logChainEvent(
  stage:
    | "capture"
    | "cart_attach"
    | "cart_attach_failed"
    | "checkout_redirect"
    | "manual",
  opts: { chainId?: string; transactionId?: string; payload?: Record<string, unknown> } = {},
): void {
  if (typeof window === "undefined") return;
  const cid = opts.chainId || getStoredAdClick()?.chainId;
  if (!cid) return;
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-gclid-chain-event`;
    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "",
      },
      body: JSON.stringify({
        chain_id: cid,
        stage,
        transaction_id: opts.transactionId,
        payload: opts.payload ?? {},
      }),
    }).catch(() => {});
  } catch {
    /* never throw from a trace */
  }
}



/**
 * Initialize ad click & UTM capture. Call once on app load.
 * Captures gclid/wbraid/gbraid AND UTM params, stores locally, and saves to DB.
 * UTM params are persisted even without a click ID so attribution survives
 * redirects and consent flows.
 */
export function initAdClickCapture(): void {
  if (typeof window === "undefined" || !isTrackingEnabled()) return;

  const params = new URLSearchParams(window.location.search);
  const gclid = params.get("gclid");
  const wbraid = params.get("wbraid");
  const gbraid = params.get("gbraid");
  const hasClickId = !!(gclid || wbraid || gbraid);
  const hasUtm = !!params.get("utm_source");

  // Persist UTMs even without click ID (e.g. email campaigns)
  // but only save to DB if we have an actual click identifier
  if (!hasClickId && !hasUtm) return;

  // If we only have UTMs (no click ID), persist them but don't save to ad_click_tracking
  if (!hasClickId && hasUtm) {
    try {
      const existing = getStoredAdClick();
      // Don't overwrite an existing click ID with UTM-only data
      if (existing && (existing.gclid || existing.wbraid || existing.gbraid)) return;
      const sessionId = getOrCreateSessionId();
      const utmData: AdClickData = {
        utm_source: params.get("utm_source") || undefined,
        utm_medium: params.get("utm_medium") || undefined,
        utm_campaign: params.get("utm_campaign") || undefined,
        utm_content: params.get("utm_content") || undefined,
        utm_term: params.get("utm_term") || undefined,
        referrer: document.referrer || undefined,
        landingPage: window.location.pathname + window.location.search,
        sessionId,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(utmData));
    } catch { /* silent */ }
    return;
  }

  // First-touch guard (matches the pre-hydration snippet in index.html):
  // if a valid click ID is already stored, do NOT overwrite. Google's
  // attribution model gives credit to the first click in the lookback
  // window for offline conversions, so overwriting on later visits would
  // misattribute revenue. We DO still want to refresh UTM-only stores
  // when a real click ID arrives (handled by the UTM-only branch above
  // which returns early when an existing click ID is present).
  const existingFirstTouch = getStoredAdClick();
  if (existingFirstTouch && (existingFirstTouch.gclid || existingFirstTouch.wbraid || existingFirstTouch.gbraid)) {
    // Still ensure the cookie is set (covers case where localStorage
    // was hydrated by the inline snippet but the cookie was blocked then).
    try {
      if (existingFirstTouch.gclid && !document.cookie.includes('_gcl_aw=')) {
        const expires = new Date(Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `_gcl_aw=${existingFirstTouch.gclid}; expires=${expires}; path=/; SameSite=Lax; Secure`;
      }
    } catch { /* cookie blocked */ }
    return;
  }

  const sessionId = getOrCreateSessionId();
  const chainId = `cid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const clickData: AdClickData = {
    gclid: gclid || undefined,
    wbraid: wbraid || undefined,
    gbraid: gbraid || undefined,
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_content: params.get("utm_content") || undefined,
    utm_term: params.get("utm_term") || undefined,
    referrer: document.referrer || undefined,
    landingPage: window.location.pathname + window.location.search,
    sessionId,
    chainId,
    timestamp: Date.now(),
  };


  // Store in localStorage (persists across page loads)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(clickData));
  } catch {
    // localStorage full or disabled — continue anyway
  }

  // Also set a cookie for cross-subdomain access (90 days)
  try {
    const expires = new Date(Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000).toUTCString();
    if (gclid) {
      document.cookie = `_gcl_aw=${gclid}; expires=${expires}; path=/; SameSite=Lax; Secure`;
    }
  } catch {
    // Cookie setting failed
  }

  // Push to dataLayer for GTM
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "ad_click_captured",
    chain_id: chainId,
    ad_click: {
      gclid: gclid || undefined,
      wbraid: wbraid || undefined,
      gbraid: gbraid || undefined,
    },
  });

  // Trace event for the chain (capture stage — SPA path).
  // Best-effort, never blocks. The pre-hydration snippet in index.html may
  // already have logged a capture row; duplicates by chain_id are fine and
  // make the SPA-vs-prehydration race observable.
  void logChainEvent("capture", {
    payload: {
      gclid: gclid || null,
      gbraid: gbraid || null,
      wbraid: wbraid || null,
      landing_page: clickData.landingPage,
      session_id: sessionId,
      captured_by: "spa-init-ad-click-capture",
    },
    chainId,
  });


  // Save to database (fire and forget — don't block the page)
  saveAdClickToDb(clickData).then(() => {
    // Delayed backfill: GA4 may not have created _ga cookie yet at page load.
    // Wait 2s, then update the row with ga_client_id if it's now available.
    setTimeout(async () => {
      try {
        const gaCookie = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.([^;]+)/);
        if (gaCookie?.[1]) {
          await supabase
            .from("ad_click_tracking" as any)
            .update({ ga_client_id: gaCookie[1] } as any)
            .eq("session_id", clickData.sessionId)
            .is("ga_client_id", null);
        }
      } catch { /* silent */ }
    }, 2000);
  }).catch(() => {
    // Silent fail — localStorage is the primary store
  });

  // Observability: log to tracking_events_log so we can confirm capture worked
  void import("./attributionLogger").then(({ logAttributionEvent }) => {
    logAttributionEvent({
      event_name: "attribution.click_captured",
      status: "success",
      event_id: sessionId,
      payload_summary: {
        has_gclid: !!gclid,
        has_wbraid: !!wbraid,
        has_gbraid: !!gbraid,
        landing_page: clickData.landingPage.slice(0, 200),
      },
    });
  }).catch(() => {});
}

/** Associate an email with the current ad click (call on checkout/login) */
export async function associateEmailWithAdClick(email: string): Promise<void> {
  if (!isTrackingEnabled()) return;

  const data = getStoredAdClick();
  if (!data) return;
  if (!data.gclid && !data.wbraid && !data.gbraid) return;

  const cleanEmail = email.toLowerCase().trim();

  // Match by click identifier (more reliable than session_id which can be lost
  // when the user returns from Shopify checkout in a new tab/session).
  try {
    const table = supabase.from("ad_click_tracking" as any);
    const updates: Promise<unknown>[] = [];
    const run = (q: any) => Promise.resolve(q).catch((e) => e);
    if (data.gclid) updates.push(run(table.update({ email: cleanEmail } as any).eq("gclid", data.gclid)));
    if (data.wbraid) updates.push(run(supabase.from("ad_click_tracking" as any).update({ email: cleanEmail } as any).eq("wbraid", data.wbraid)));
    if (data.gbraid) updates.push(run(supabase.from("ad_click_tracking" as any).update({ email: cleanEmail } as any).eq("gbraid", data.gbraid)));
    updates.push(run(supabase.from("ad_click_tracking" as any).update({ email: cleanEmail } as any).eq("session_id", data.sessionId)));
    await Promise.all(updates);
  } catch (err) {
    console.warn("[AdClick] Failed to associate email:", err);
  }
}

/**
 * Clear session-scoped attribution data from localStorage after a confirmed purchase.
 * The ad click data (zooi_ad_click) is intentionally kept — it has its own 90-day expiry
 * and may be needed for post-purchase attribution windows.
 */
export function clearSessionAttribution(): void {
  try {
    localStorage.removeItem(LS_SESSION_KEY);
    localStorage.removeItem(LS_LANDING_KEY);
    localStorage.removeItem(LS_SOURCE_KEY);
    sessionStorage.removeItem("zooi_session_id");
    sessionStorage.removeItem("zooi_landing_page");
    sessionStorage.removeItem("zooi_first_source");
  } catch { /* silent */ }
}

/** Save ad click data to the database */
async function saveAdClickToDb(data: AdClickData): Promise<void> {
  // Try to capture ga_client_id from _ga cookie at click time
  let gaClientId: string | null = null;
  try {
    const gaCookie = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.([^;]+)/);
    if (gaCookie?.[1]) gaClientId = gaCookie[1];
  } catch { /* cookie access blocked */ }

  try {
    const { error } = await supabase
      .from("ad_click_tracking" as any)
      .insert({
        gclid: data.gclid || null,
        wbraid: data.wbraid || null,
        gbraid: data.gbraid || null,
        landing_page: data.landingPage,
        session_id: data.sessionId,
        ga_client_id: gaClientId,
      } as any);

    if (error) {
      // Might be a duplicate — that's fine
      console.warn("[AdClick] DB save failed:", error.message);
    }
  } catch {
    // Silent fail
  }
}
